import * as React from "react";
import posthog from "posthog-js";
import * as Sentry from "@sentry/gatsby";
import {
  CheckoutAnswers,
  coreapi,
  ServerUser,
  UpsertWebSessionInput,
} from "@base/services/coreapi";
import {
  upsertWebSession as upsertWebSessionService,
  whenWebSessionSettled,
} from "@base/services/webSession";
import { browserLocalStorage, BrowserLocalStorageState } from "@base/browserLocalStorage";
import { useBrowserLocalStorage } from "@base/useBrowserLocalStorage";
import { identifyAdUser } from "@base/lib/tracking-lib";
import { getSiteConfig } from "@base/config";
import {
  getCheckoutEmail,
  getCheckoutToken,
  setCheckoutEmail,
  setCheckoutToken,
  takeArrivalJumpToken,
} from "@base/lib/checkoutSession";
import {
  PaymentConfig,
  PaymentConfigMap,
  PricingPlan,
  UserGeolocation,
  UpsellProduct,
} from "@base/types";
import { modifyPricingPlans, modifyUpsellProducts } from "@base/utils/products";
import {
  formatPrice as formatPricePure,
  formatPriceStructured as formatPriceStructuredPure,
} from "@base/utils/formatPrice";
import { getQuizValues, QuizQuestionsState } from "@base/lib/quiz-lib";
import { klaviyo } from "@base/lib/tracking-lib";
import { PartnerType, RootStoreState } from "./types";
import { consumeURLParams } from "./consumeURLParams";
import { getPaymentConfig } from "./paymentConfig";
import {
  DEFAULT_OFFER_TIME,
  DEFAULT_UPSELL_OFFER_TIME,
  DEFAULT_GEOLOCATION,
  DEFAULT_PAYMENT_CONFIG,
} from "./defaults";

export const RootStoreContext = React.createContext<RootStoreState | null>(
  null,
);

// Identity side of useEnsureUser's handlePostSubmit, for a user loaded from the
// checkout session (minus the lead events, which already fired on the funnel
// domain): a cross-domain arrival starts with fresh anonymous analytics
// identities, so re-attach them to the restored user. Klaviyo is covered by the
// knownEmail effect below.
// With no user (the email was taken), the checkout's email is the only key.
function identifyRestoredUser(user: ServerUser | null, checkoutEmail: string | null): void {
  const rawEmail = user?.email ?? checkoutEmail;
  if (!rawEmail) return;
  const email = rawEmail.toLowerCase().trim();
  posthog.setPersonProperties({ email, ...(user ? { user_id: user.id } : {}) });
  if (user) posthog.alias(user.id);
  Sentry.setUser({ ...(user ? { id: user.id } : {}), email });
  // Advanced matching, so checkout-domain pixel events carry the email.
  identifyAdUser({ email, userID: user?.id });
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

// The server's blob is untyped JSON: keep only what the checkout page reads.
function toCheckoutAnswers(value: unknown): CheckoutAnswers | null {
  if (!isRecord(value)) return null;
  const answers: CheckoutAnswers = {};
  if (typeof value.name === "string") answers.name = value.name;
  if (isRecord(value.custom_properties)) answers.custom_properties = value.custom_properties;
  return answers;
}

export function RootStore(props: React.PropsWithChildren) {
  const [geolocation, setGeolocation] =
    React.useState<UserGeolocation>(DEFAULT_GEOLOCATION);
  const [isGeolocationLoading, setIsGeolocationLoading] = React.useState(true);
  const [offerTime, setOfferTime] = React.useState<number>(DEFAULT_OFFER_TIME);
  const [upsellOfferTime, setUpsellOfferTime] = React.useState<number>(
    DEFAULT_UPSELL_OFFER_TIME,
  );

  const localStorage = useBrowserLocalStorage();

  const user = localStorage.state.user;

  const partnerGender = React.useMemo<PartnerType | undefined>(() => {
    const qs = localStorage.state.quizState;
    if (!qs) return undefined;
    // Serialization boundary: apps may alias @base/lib/quiz-lib to a local fork,
    // so the persisted state is narrowed structurally here.
    return getQuizValues<{
      "partner-type":
        | "male"
        | "female"
        | "both"
        | "multiple-males"
        | "multiple-females"
        | "other";
    }>(qs as unknown as QuizQuestionsState)["partner-type"];
  }, [localStorage.state.quizState]);

  const [paymentConfig, setPaymentConfig] = React.useState<PaymentConfig>(
    DEFAULT_PAYMENT_CONFIG,
  );
  const paymentConfigInputRef = React.useRef<{
    paymentConfigMap: PaymentConfigMap;
    defaultPaymentConfig: PaymentConfig;
    defaultCurrency: string;
  } | null>(null);

  const [pageConfigInitialized, setPageConfigInitialized] =
    React.useState(false);
  /** Preselected avatar/bot passed in via ?initialAvatarID (used by bot-builder funnels). */
  const [initialAvatarID, setInitialAvatarID] = React.useState<string | null>(
    null,
  );
  const [rawPricingPlans, setRawPricingPlans] = React.useState<PricingPlan[]>(
    [],
  );
  const [rawUpsellProducts, setRawUpsellProducts] = React.useState<
    UpsellProduct[]
  >([]);
  const [selectedPricingPlanID, setSelectedPricingPlanID] = React.useState<
    string | null
  >(null);

  // Init: consume URL params, restore user
  React.useEffect(() => {
    async function init() {
      const params = consumeURLParams();

      const localStorageUpdates: Partial<BrowserLocalStorageState> = {};

      if (params.devMode !== undefined) {
        localStorageUpdates.devMode = params.devMode;
      }

      if (params.couponID) {
        localStorageUpdates.couponID = params.couponID;
      }

      if (params.initialAvatarID) {
        setInitialAvatarID(params.initialAvatarID);
      }

      // Old first-time login token: replaced by the handoff code, must not linger.
      if (browserLocalStorage.get().authToken !== undefined) {
        localStorageUpdates.authToken = undefined;
      }

      if (Object.keys(localStorageUpdates).length > 0) {
        localStorage.update(localStorageUpdates);
      }

      try {
        // Cross-domain arrival: swap the jump token for this origin's own
        // checkout session.
        const jumpToken = takeArrivalJumpToken();
        let redeemed = false;
        if (jumpToken) {
          const res = await coreapi.redeemCheckout(jumpToken);
          if (res.error) {
            console.error("Checkout redeem failed", { status: res.error.status });
          } else {
            setCheckoutToken(res.data.checkoutToken);
            // After setCheckoutToken, which clears the email. The funnel's
            // email, so the buyer does not type it again (C2).
            if (res.data.email) setCheckoutEmail(res.data.email);
            redeemed = true;
          }
        }

        // A checkout with no user and a known email has nothing more to load.
        const nothingKnown = !localStorage.state.user && !getCheckoutEmail();
        if (getCheckoutToken() && (redeemed || nothingKnown)) {
          const me = await coreapi.getCheckoutMe();
          if (me.error) {
            console.error("Failed to load the checkout", { status: me.error.status });
            return;
          }
          const { user, email, answers } = me.data;
          // undefined drops a user an earlier checkout left behind.
          localStorage.update({ user: user ?? undefined, checkoutAnswers: toCheckoutAnswers(answers) });
          // Fallback only: a sorted email is newer than the checkout's.
          const knownEmail = email ?? user?.email ?? null;
          if (knownEmail && !getCheckoutEmail()) setCheckoutEmail(knownEmail);
          identifyRestoredUser(user, getCheckoutEmail());
        }
      } catch (err) {
        console.error("Failed to restore the checkout user", err);
      }
    }

    init();
  }, []);

  // Identify the visitor in Klaviyo as soon as their email is known (restored
  // from local storage on load, or set after they submit the email step). This
  // establishes the Klaviyo profile once, so every klaviyo.trackEvent(...) call
  // afterwards attaches to it — no need to identify at each call site.
  const knownEmail = user?.email ?? localStorage.state.checkoutEmail ?? null;
  React.useEffect(() => {
    if (knownEmail) {
      klaviyo.identifyUser({ email: knownEmail });
    }
  }, [knownEmail]);

  // Start waiting for the pageload's session reply (`h`) once per pageload,
  // so a page reached later by in-app navigation already has it — no loader
  // there (see useRiskHit). After hydration, so hydration never sees it settled.
  React.useEffect(() => {
    whenWebSessionSettled();
  }, []);

  // Fetch geolocation
  React.useEffect(() => {
    async function fetchGeolocation() {
      // No backend configured (GATSBY_CORE_URL unset): nothing to ask.
      if (!getSiteConfig().coreApiHost) return;

      try {
        const res = await coreapi.getUserGeolocationInfo();

        if (res.error) {
          console.error("Failed to fetch user geolocation info");
          return;
        }

        if (!res.data) {
          console.error("Geolocation data is missing");
          return;
        }

        if (!res.data.countryCode) {
          console.error("Country code data is missing");
          return;
        }

        setGeolocation(res.data);
      } finally {
        setIsGeolocationLoading(false);
      }
    }

    fetchGeolocation();
  }, []);

  // Resolve payment config when geolocation changes
  React.useEffect(() => {
    if (!paymentConfigInputRef.current) return;
    const { paymentConfigMap, defaultPaymentConfig, defaultCurrency } =
      paymentConfigInputRef.current;
    setPaymentConfig(
      getPaymentConfig(
        geolocation.countryCode,
        paymentConfigMap,
        defaultPaymentConfig,
        defaultCurrency,
      ),
    );
  }, [geolocation.countryCode]);

  const couponID = React.useMemo(
    () => localStorage.state.couponID ?? undefined,
    [localStorage.state.couponID],
  );

  // Quiz restarts wipe local storage, but never the web session: it is the
  // session's identity (the sid fallback in `getSid`) and carries the backend's
  // risk answer `h`, not quiz state. Writing it back in the same set also means
  // an upsert reply that landed just before the wipe is not lost.
  const clearBrowserLocalStorageState = React.useCallback(() => {
    const { webSession } = browserLocalStorage.get();
    browserLocalStorage.set(webSession ? { webSession } : {});
  }, []);

  // All persistence (localStorage mirror, handoff-cache drop, ?sid= URL strip)
  // runs inside the service, and the browserLocalStorage subscription mirrors
  // the write into React state — this wrapper only reports failures.
  const upsertWebSession = React.useCallback(
    async (input: Omit<UpsertWebSessionInput, "url">) => {
      const response = await upsertWebSessionService(input);

      if (response.error) {
        console.error(
          "Failed to upsert web session:",
          response.error.value.message,
        );
      }
    },
    [],
  );

  const initPageConfig = React.useCallback(
    (input: {
      pricingPlans: PricingPlan[];
      upsellProducts: UpsellProduct[];
      preselectedPricingPlanID?: string;
      paymentConfigMap: PaymentConfigMap;
      defaultPaymentConfig: PaymentConfig;
      defaultCurrency?: string;
    }) => {
      if (pageConfigInitialized) return;

      const defaultCurrency = input.defaultCurrency ?? "usd";
      paymentConfigInputRef.current = {
        paymentConfigMap: input.paymentConfigMap,
        defaultPaymentConfig: input.defaultPaymentConfig,
        defaultCurrency,
      };

      setPaymentConfig(
        getPaymentConfig(
          geolocation.countryCode,
          input.paymentConfigMap,
          input.defaultPaymentConfig,
          defaultCurrency,
        ),
      );
      setRawPricingPlans(input.pricingPlans);
      setRawUpsellProducts(input.upsellProducts);
      setSelectedPricingPlanID(input.preselectedPricingPlanID ?? null);
      setPageConfigInitialized(true);
    },
    [pageConfigInitialized, geolocation.countryCode],
  );

  const formatPrice = React.useCallback(
    (amount: number) => formatPricePure(amount, paymentConfig),
    [paymentConfig],
  );

  const formatPriceStructured = React.useCallback(
    (amount: number) => formatPriceStructuredPure(amount, paymentConfig),
    [paymentConfig],
  );

  const pricingPlans = React.useMemo<PricingPlan[]>(() => {
    return modifyPricingPlans(rawPricingPlans, {
      promotionID: localStorage.state.acceptedPromotionID,
      currency: paymentConfig.currency,
      priceModifierFactor: paymentConfig.priceModifierFactor_experimental,
      minorUnitAmountRounding:
        paymentConfig.minorUnitAmountRounding_experimental,
    });
  }, [
    rawPricingPlans,
    localStorage.state.acceptedPromotionID,
    paymentConfig.currency,
    paymentConfig.priceModifierFactor_experimental,
    paymentConfig.minorUnitAmountRounding_experimental,
  ]);

  const upsellProducts = React.useMemo<UpsellProduct[]>(() => {
    return modifyUpsellProducts(rawUpsellProducts, {
      currency: paymentConfig.currency,
    });
  }, [rawUpsellProducts, paymentConfig.currency]);

  const value = React.useMemo<RootStoreState>(() => {
    return {
      geolocation,
      isGeolocationLoading,
      offerTime,
      setOfferTime,
      upsellOfferTime,
      setUpsellOfferTime,
      user,
      partnerGender,
      couponID,
      browserLocalStorageState: localStorage.state,
      setBrowserLocalStorageState: localStorage.update,
      clearBrowserLocalStorageState,
      upsertWebSession,
      paymentConfig,
      formatPrice,
      formatPriceStructured,
      pageConfigInitialized,
      pricingPlans,
      upsellProducts,
      selectedPricingPlanID,
      setSelectedPricingPlanID,
      initPageConfig,
      initialAvatarID,
    };
  }, [
    geolocation,
    isGeolocationLoading,
    offerTime,
    upsellOfferTime,
    user,
    partnerGender,
    couponID,
    localStorage.state,
    localStorage.update,
    clearBrowserLocalStorageState,
    upsertWebSession,
    paymentConfig,
    formatPrice,
    formatPriceStructured,
    pageConfigInitialized,
    pricingPlans,
    upsellProducts,
    selectedPricingPlanID,
    initPageConfig,
    initialAvatarID,
  ]);

  return (
    <RootStoreContext.Provider value={value}>
      {props.children}
    </RootStoreContext.Provider>
  );
}
